import React, { forwardRef, useContext, useEffect, useMemo, useState } from 'react'
import { useForm, FormProvider, useFormState } from 'react-hook-form'
import { useLoad } from '../hooks/load'
import { IconClickReload } from '../icon/icon-click'
import { lastUpdatedText } from '../date/last-updated'
import { SaveFailure, useSaveFailure } from '../error/save-failure'

export const FormContext = React.createContext()

export const useFormDisable = () => {
  const { formEnabled } = useContext(FormContext)
  const { isSubmitting } = useFormState()

  return useMemo(() => !formEnabled || isSubmitting, [formEnabled, isSubmitting])
}

// onInvalid (optional) runs instead of onSubmit when validation blocks the
// submit, with react-hook-form's errors object. A failure onSubmit lets
// through is shown under the form (shared/error/save-failure.js).
export const FormNew = forwardRef(({ children, state, enabled, onSubmit, onInvalid, onRefresh }, ref) => {
  const formMethods = useForm({ defaultValues: state || {} })

  let lastUpdated = useMemo(() => (state && state.updatedAt ? lastUpdatedText(state.updatedAt) : null), [state])
  const isSubmiting = useMemo(() => formMethods.formState.isSubmitting, [formMethods.formState.isSubmitting])

  const [formEnabled, updateFormEnabled] = useState(enabled)

  useLoad(() => formMethods.reset(state), [formMethods, state])

  useEffect(
    () => {
      updateFormEnabled(enabled)
    },
    [enabled]
  )

  // A save that fails shows its message and reference here, and the page
  // stays put with the input as typed (shared/error/save-failure.js).
  const [failure, submit, dismissFailure] = useSaveFailure(onSubmit)

  return (
    <FormContext.Provider value={{ formEnabled, updateFormEnabled }}>
      <FormProvider {...formMethods}>
        <form method="post" onSubmit={formMethods.handleSubmit(submit, onInvalid)}>
          {children}
        </form>
        <SaveFailure failure={failure} onDismiss={dismissFailure} />
        <div className="d-flex">
          <small className="flex-fill" />
          {lastUpdated &&
            <small>
              last updated {lastUpdated}
            </small>}
          <small>
            {isSubmiting && <span className="mx-1 spinner-border spinner-border-sm" />}
            {!isSubmiting && onRefresh && <IconClickReload onClick={onRefresh} />}
          </small>
        </div>
      </FormProvider>
    </FormContext.Provider>
  )
})
